import Choices, { Options } from "choices.js";

import {
  LanguageService,
  TranslationTexts,
} from "../services/language-service.js";
import { pipe } from "fp-ts/lib/function.js";
import { either } from "fp-ts";

function makeSettings(
  placeholder: string | undefined,
  searchEnabled: boolean,
  translationObject: TranslationTexts
) {
  const localizedChoicesTexts = {
    loadingText: translationObject.choicesJs.loadingText,
    noResultsText: translationObject.choicesJs.noResultsText,
    noChoicesText: translationObject.choicesJs.noChoicesText,
    itemSelectText: translationObject.choicesJs.itemSelectText,
    uniqueItemText: translationObject.choicesJs.uniqueItemText,
    customAddItemText: translationObject.choicesJs.customAddItemText,
    addItemText: translationObject.choicesJs.addItemText,
    maxItemText: translationObject.choicesJs.maxItemText,
    searchPlaceholderValue: translationObject.choicesJs.searchPlaceholderValue,
  };

  return {
    closeDropdownOnSelect: true,
    allowHTML: true,
    shouldSort: false,
    removeItemButton: true,
    removeItemIconText: "",
    placeholder: true,
    placeholderValue: placeholder ?? null,
    searchEnabled: searchEnabled ?? true,
    searchFields: ["label"],
    fuseOptions: { includeScore: true, threshold: 0.2, ignoreLocation: true },
    ...localizedChoicesTexts,
  };
}

/**
 *
 * @param {LanguageService} languageService
 */
export function initializeSelectElements(
  languageService: LanguageService,
  root: Element = document.documentElement
) {
  const translations = languageService.getTranslations();

  function createChoices(
    element: Element & { choices?: Choices },
    settings: Partial<Options> | undefined
  ) {
    if (element.choices) {
      element.choices.destroy();
    }
    let choicesInstance = new Choices(element, settings);
    element.choices = choicesInstance;
  }

  function setupSelectWithPlaceholder(placeholder: string) {
    return (selectElement: Element) => {
      const settings = makeSettings(placeholder, true, translations);

      createChoices(selectElement, settings);
    };
  }

  function initializeChoicesInDetails(detailsElement: HTMLDetailsElement) {
    detailsElement
      .querySelectorAll('select[name^="et_pb_contact_dag_"]')
      .forEach(
        setupSelectWithPlaceholder(translations.bookMeetings.placeholderDay)
      );
    detailsElement
      .querySelectorAll('select[name^="et_pb_contact_tidspunkt_"]')
      .forEach(
        setupSelectWithPlaceholder(translations.bookMeetings.placeholderTime)
      );
    detailsElement
      .querySelectorAll<HTMLElement>(".ug select")
      .forEach((selectElement: HTMLElement) => {
        const placeholder = selectElement.dataset?.placeholder;
        const searchEnabled = Boolean(selectElement.dataset?.searchEnabled);
        const settings = makeSettings(placeholder, searchEnabled, translations);
        createChoices(selectElement, settings);
      });
  }

  // Initialiser alle <select>-elementer uden for <details>
  root
    .querySelectorAll('select[name^="et_pb_contact_dag_"]')
    .forEach(
      setupSelectWithPlaceholder(translations.bookMeetings.placeholderDay)
    );
  root
    .querySelectorAll('select[name^="et_pb_contact_tidspunkt_"]')
    .forEach(
      setupSelectWithPlaceholder(translations.bookMeetings.placeholderTime)
    );
  root
    .querySelectorAll<HTMLElement>(".ug select")
    .forEach((selectElement: HTMLElement) => {
      const placeholder = selectElement.dataset?.placeholder;
      const searchEnabled = Boolean(selectElement.dataset?.searchEnabled);
      const settings = makeSettings(placeholder, searchEnabled, translations);
      createChoices(selectElement, settings);
    });

  // Find alle <details>-elementer og tilføj toggle-event listeners
  root
    .querySelectorAll("details")
    .forEach((detailsElement: HTMLDetailsElement) => {
      detailsElement.addEventListener("toggle", function () {
        if (detailsElement.open) {
          initializeChoicesInDetails(detailsElement);
        }
      });
    });
}
